// The "Model" section: Photon's ModelSelectionDropdown (LabeledDropdown +
// ThemeV2LookAndFeel popup, design nodes 6005:16011 / 6253:7780). A native
// <select> cannot draw section headers, version tags or the check glyph, so
// this is a button + listbox popup. Keyboard handling lives in the host
// (it owns the highlight state); items are not focusable — focus stays on the
// button, which announces the highlighted option via aria-activedescendant.
// The app's "More models..." action (opens its Model Manager) has no
// equivalent here and is left out.
import { html, nothing } from 'lit';
import type { TemplateResult } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import type { MenuSection, ModelDef } from '@/state/models';
import { carrotIcon, checkSmallIcon } from './icons';

export interface PickerProps {
  current: ModelDef;
  open: boolean;
  highlightId: string | null;   // keyboard highlight (model id)
  sections: Array<{ title: MenuSection; models: ModelDef[] }>;
  disabled: boolean;
}

export interface PickerCallbacks {
  onToggle: () => void;
  onSelect: (id: string) => void;
  onHighlight: (id: string | null) => void;
  onKeydown: (e: KeyboardEvent) => void;
}

export const optionDomId = (id: string): string => `model-opt-${id}`;

export function renderModelPicker(p: PickerProps, cb: PickerCallbacks): TemplateResult {
  const item = (m: ModelDef) => {
    const selected = m.id === p.current.id;
    return html`
      <div
        id=${optionDomId(m.id)}
        class=${classMap({ 'popup-item': true, selected, highlight: p.highlightId === m.id })}
        role="option"
        aria-selected=${selected ? 'true' : 'false'}
        @pointerenter=${() => cb.onHighlight(m.id)}
        @pointerdown=${(e: PointerEvent) => { e.preventDefault(); cb.onSelect(m.id); }}
      >
        <span class="popup-name">${m.label}</span>
        <span class="version">${m.version}</span>
        ${selected ? html`<span class="glyph">${checkSmallIcon}</span>` : nothing}
      </div>
    `;
  };

  const popup = p.open
    ? html`
        <div
          class="popup"
          role="listbox"
          id="model-listbox"
          aria-label="Model"
          @pointerleave=${() => cb.onHighlight(null)}
        >
          ${p.sections.map(
            (sec) => html`
              <div class="popup-header" role="presentation">${sec.title}</div>
              ${sec.models.map(item)}
            `,
          )}
        </div>
      `
    : nothing;

  return html`
    <div class="model-section">
      <span class="section-label" id="model-label">Model</span>
      <div class="picker">
        <button
          class=${classMap({ 'input-row': true, 'picker-btn': true, open: p.open })}
          type="button"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded=${p.open ? 'true' : 'false'}
          aria-controls="model-listbox"
          aria-labelledby="model-label"
          aria-activedescendant=${p.open && p.highlightId ? optionDomId(p.highlightId) : nothing}
          ?disabled=${p.disabled}
          @click=${cb.onToggle}
          @keydown=${cb.onKeydown}
        >
          <span class="picker-name">${p.current.label}</span>
          <span class="version">${p.current.version}</span>
          ${carrotIcon}
        </button>
        ${popup}
      </div>
    </div>
  `;
}
